import { useState, useEffect } from "react";
import { ChevronDown, MessageSquare, ShieldCheck, Coins, Zap, Lock, HelpCircle, Layers } from "lucide-react";

const FAQ_DATA = [
  {
    category: "Getting started",
    icon: HelpCircle,
    items: [
      {
        q: "What is Stakeforge and how is it different from staking natively?",
        a: "Stakeforge is a non-custodial liquid-staking aggregator routing your delegation through a vetted mesh of 34 institutional validators. We pool MEV revenue, priority-fee surplus and block-space auction rewards across all delegators, then auto-compound the proceeds back into your position every 12 hours. The net effect is a more stable, typically higher APR than going to a single validator yourself — without locking your assets behind a 21-day unbonding period."
      },
      {
        q: "Do I need to create an account?",
        a: "No. Stakeforge is fully self-custodial — there is no email, password or KYC for standard use. You connect a supported wallet (MetaMask, WalletConnect, Coinbase Wallet, Phantom, Solflare, or Backpack), sign a staking transaction, and you're done. Your positions, rewards and history are all tied to your on-chain address."
      },
      {
        q: "Which wallets and networks are supported?",
        a: "On EVM we support MetaMask, WalletConnect (300+ wallets) and Coinbase Wallet — across Ethereum mainnet, Arbitrum, Optimism, Polygon and BNB Chain. On Solana we support Phantom, Solflare and Backpack on mainnet-beta. Cross-chain bridging is handled internally by the validator mesh; you only see the asset you started with."
      },
      {
        q: "What is the minimum I can stake?",
        a: "Minimums are token-specific and visible on every pool page. Liquid majors (ETH, SOL, BTC.b) start at 0.01–0.1 tokens. Mid-cap governance tokens typically start at 1–10. Memecoin pools have higher minimums to make rounding-error-proof reward accounting (e.g. 100,000 BONK). There is no maximum."
      }
    ]
  },
  {
    category: "APR, rewards & boost pools",
    icon: Coins,
    items: [
      {
        q: "Why is your APR higher than what I see on the native network?",
        a: "Three layered revenue streams that a single validator cannot offer on its own: (1) MEV smoothing — our 34 partner validators contribute their priority-fee + MEV revenue to a shared pool that pays out evenly, eliminating the 'lucky block' variance you get solo-staking; (2) 12-hour auto-compounding — rewards are reinvested twice a day, so the displayed APR is effectively APY; (3) Validator partnership rebates — partners pay us a flat fee for guaranteed delegation flow, which we route back to delegators as a yield boost. Together these typically deliver 2–4× the headline rate of native staking, fully on-chain and provable."
      },
      {
        q: "How exactly does the 24-hour Boost Pool work?",
        a: "The Boost Pool captures three short-cycle revenue streams that don't fit the standard 12h smoothing window: validator priority auctions (institutional flow paying premium for guaranteed inclusion), MEV searcher bounties (flat fees paid for early-confirmation slots), and priority-fee surplus from congested blocks. The pool settles every day at 00:00 UTC. There is no rollover — you must opt back in each cycle. Yields swing with real auction demand and can be 4–8× the standard pool. Capacity is capped at 12% of TVL per asset to keep payouts meaningful."
      },
      {
        q: "When do I receive rewards? Do I have to claim them manually?",
        a: "Standard pool rewards auto-compound into your position every 12 hours — you don't have to do anything. They are visible immediately in the 'Pending rewards' counter as they accrue, but the on-chain settlement happens twice a day for gas efficiency. You can also click 'Claim' at any time to move accrued rewards to your wallet balance instead of reinvesting them. Boost pool rewards settle once a day at 00:00 UTC into your wallet directly."
      },
      {
        q: "Is the APR fixed or variable?",
        a: "Variable, like all real staking yields. The headline rate you see is a 14-day trailing realised APR — what historical delegators actually earned, not a marketing number. We do not smooth or guarantee yields. The realised rate moves with network activity, validator performance and MEV demand. The 30-day APR sparkline on every pool page shows the actual variance."
      },
      {
        q: "What happens if the APR for a pool changes after I stake?",
        a: "Your future rewards will simply earn at the new rate. There is no lock-in on the rate. If yields drop materially, you can move to a different pool — or unstake entirely — without penalty (subject to the lockup period of the specific pool you're in)."
      }
    ]
  },
  {
    category: "Security, audits & risk",
    icon: ShieldCheck,
    items: [
      {
        q: "Has Stakeforge been audited?",
        a: "Yes. The core staking router and reward distributor have been audited by Trail of Bits (Q4 2024) and OpenZeppelin (Q1 2025). Both reports are public and linked under 'Security audits' on every pool page. The 24h Boost auction contract was audited separately by Spearbit in Q3 2025. We run an ongoing $250k Immunefi bug bounty for any newly discovered critical issue."
      },
      {
        q: "What is slashing and am I protected from it?",
        a: "Slashing is a network-level penalty applied to validators that misbehave (double-signing, prolonged downtime). On Ethereum, the maximum slash for a single offence is roughly 0.5–1 ETH per validator. We protect delegators through an on-chain insurance vault funded by 2.5% of all pool revenue — currently sized at ~$4.2M. In the unlikely event of a slash, losses are reimbursed to affected positions, capped at 100% of stake. Every pool page shows the live insurance-vault balance for that asset."
      },
      {
        q: "Can Stakeforge steal my funds?",
        a: "No. Stakeforge never custodies your principal. Your stake is held in an on-chain contract that you (and only you) can withdraw from with your wallet's signature. We can pause new deposits in an emergency, but we cannot move existing positions. Even if our front-end disappeared tomorrow, you could withdraw directly from the contracts using the published ABIs."
      },
      {
        q: "Who runs the validator mesh? Are they all run by Stakeforge?",
        a: "No — that would defeat the point. The mesh is a partnership of 34 independent enterprise validator operators (Coinbase Cloud, Kraken, Allnodes, Chorus One, Figment, P2P, Staking Facilities and 27 others). Each runs its own infrastructure. Stakeforge does not run any validators directly. We route delegations across the mesh based on real-time performance and slashing risk, weighted by validator stake share."
      }
    ]
  },
  {
    category: "Lockups, withdrawals & fees",
    icon: Lock,
    items: [
      {
        q: "How long does unstaking take?",
        a: "It depends on the asset. Liquid pools (ETH, SOL, BNB, MATIC, LDO, ARB, OP, FRAX, ENS, WBTC) are flexible — you can withdraw instantly. Mid-cap pools have a 7-14 day unbonding period inherited from the underlying network. Memecoin and governance-heavy pools (CRV, SNX, CVX, FXS, GMX) can have up to 21 or 28 days. The lockup is always shown on the pool card before you stake."
      },
      {
        q: "What fees does Stakeforge charge?",
        a: "A single 2.5% performance fee on rewards earned — never on principal. The fee is collected automatically and funds the slashing insurance vault. There are no deposit fees, no withdrawal fees, and no management fees. Network gas (paid to the chain itself, not us) varies with congestion."
      },
      {
        q: "Can I unstake mid-cycle if I need my money urgently?",
        a: "Liquid pools: yes, instantly. Locked pools: yes, but a fast-exit window costs 0.5% of the unstaked amount in addition to forfeiting that cycle's rewards. The fast-exit fee is paid to delegators staying in the pool — it's not retained by Stakeforge."
      }
    ]
  },
  {
    category: "Boost auctions & MEV",
    icon: Zap,
    items: [
      {
        q: "What exactly is being auctioned in the Boost Pool?",
        a: "Validator block-space — specifically, the right for institutional flow (market makers, CEX hot wallets, large bridges) to have transactions placed at the top of a block during periods of network congestion. Searchers and trading firms bid for these slots in a sealed-bid second-price auction every slot. The winning bid plus MEV surplus accrues to the boost pool and is distributed proportionally."
      },
      {
        q: "Is this just promising MEV that may never materialise?",
        a: "No — boost APR is realised, not projected. The number you see on the leaderboard is what the pool actually paid out in the previous cycle, calculated post-settlement. You can verify each cycle's payout against the on-chain settlement transaction shown under 'Recent activity' on each pool page."
      },
      {
        q: "Why do meme tokens have higher boost APRs than majors?",
        a: "Two reasons. First, meme tokens trade with thinner liquidity and higher volatility, which creates more profitable MEV opportunities (arbitrage, sandwich resistance, liquidations) per unit of TVL. Second, our boost-pool capacity is capped at 12% of TVL per asset — when TVL is small, the same revenue spread over fewer participants produces a higher per-share APR. Higher headline boost rates therefore come with higher yield variance."
      }
    ]
  },
  {
    category: "Tax, reporting & operations",
    icon: Layers,
    items: [
      {
        q: "Does Stakeforge report to tax authorities?",
        a: "No. As a non-custodial protocol we do not file 1099s or equivalent forms. You can export a full CSV of your stake/unstake/claim history per address from the Profile page, and you are responsible for reporting in your jurisdiction. Most countries treat staking rewards as ordinary income at fair market value on receipt — consult a qualified accountant."
      },
      {
        q: "Where is Stakeforge incorporated?",
        a: "Stakeforge Labs SA is incorporated in Zug, Switzerland under the VQF self-regulatory framework. The smart contracts are deployed by an unaffiliated foundation in the Cayman Islands and are immutable for the staking router and time-locked (48h delay) for parameter updates."
      },
      {
        q: "I have a problem — how do I get human support?",
        a: "Open a ticket via the Refer page footer link, or DM us on Discord (response time ~2h during EU/US trading hours, ~12h on weekends). For security-sensitive issues, use the PGP-encrypted address listed on the Legal page. We do not provide phone support and will never DM you first — anyone claiming to be 'Stakeforge support' in your DMs is impersonating us."
      }
    ]
  }
];

export default function FAQ() {
  const [openKey, setOpenKey] = useState("0-0");
  const [search, setSearch] = useState("");
  const [host, setHost] = useState("stakeforge.xyz");

  useEffect(() => {
    // Dynamic site domain so emails/links match wherever this is deployed.
    if (typeof window !== "undefined" && window.location && window.location.hostname) {
      const h = window.location.hostname;
      // Skip filesystem-local & ip-based hosts — keep brand fallback in those cases.
      if (h && !["localhost", "127.0.0.1", ""].includes(h) && !/^\d+\.\d+\.\d+\.\d+$/.test(h)) {
        setHost(h);
      }
    }
  }, []);

  const supportEmail = `support@${host}`;

  const filtered = FAQ_DATA.map((cat) => ({
    ...cat,
    items: cat.items.filter(
      (it) =>
        !search ||
        it.q.toLowerCase().includes(search.toLowerCase()) ||
        it.a.toLowerCase().includes(search.toLowerCase())
    )
  })).filter((cat) => cat.items.length);

  const totalCount = FAQ_DATA.reduce((a, c) => a + c.items.length, 0);

  return (
    <section className="border-t" style={{ borderColor: "var(--border)" }} id="faq" data-testid="faq-section">
      <div className="max-w-7xl mx-auto px-6 py-20 sm:py-24">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Left rail */}
          <div className="lg:col-span-4 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>
              ▍ Questions answered
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
              Everything you need.<br />
              <span className="grad-sol-text">Before you stake.</span>
            </h2>
            <p className="mt-5 text-sm" style={{ color: "var(--text-2)" }}>
              {totalCount} answers across {FAQ_DATA.length} categories. Still stuck?{" "}
              <a href={`mailto:${supportEmail}`} className="text-white underline-offset-4 hover:underline">{supportEmail}</a>{" "}
              or DM us on Discord — average reply 2 h.
            </p>

            <div className="mt-7 border flex items-center gap-2 px-3 h-11" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
              <MessageSquare size={14} style={{ color: "var(--text-3)" }} />
              <input
                type="text"
                placeholder="Search the FAQ..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="faq-search"
                className="bg-transparent border-0 outline-none flex-1 text-sm placeholder:text-neutral-600"
              />
              {search && (
                <button onClick={() => setSearch("")} className="text-xs uppercase tracking-widest" style={{ color: "var(--text-3)" }}>
                  clear
                </button>
              )}
            </div>

            <div className="mt-6 hidden lg:flex flex-col gap-2">
              {FAQ_DATA.map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => {
                      const el = document.getElementById(`faq-cat-${i}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="flex items-center gap-3 px-3 py-2.5 text-xs border lift uppercase tracking-wider text-left"
                    style={{ borderColor: "var(--border)", color: "var(--text-2)" }}
                    data-testid={`faq-nav-${i}`}
                  >
                    <Icon size={13} style={{ color: "var(--text-3)" }} />
                    {cat.category}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right column - questions */}
          <div className="lg:col-span-8">
            {filtered.length === 0 && (
              <div className="text-center py-20 text-sm" style={{ color: "var(--text-3)" }}>
                No questions match "{search}"
              </div>
            )}

            {filtered.map((cat, ci) => {
              const Icon = cat.icon;
              return (
                <div key={cat.category} id={`faq-cat-${ci}`} className="mb-10">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b" style={{ borderColor: "var(--border)" }}>
                    <Icon size={14} className="grad-sol-text" />
                    <h3 className="font-display font-bold text-sm uppercase tracking-[0.2em]">{cat.category}</h3>
                    <span className="font-mono text-[10px] ml-auto" style={{ color: "var(--text-3)" }}>{cat.items.length} answers</span>
                  </div>

                  <ul className="flex flex-col">
                    {cat.items.map((item, ii) => {
                      const key = `${ci}-${ii}`;
                      const open = openKey === key;
                      return (
                        <li
                          key={key}
                          className="border-b transition-colors"
                          style={{ borderColor: "var(--border)" }}
                          data-testid={`faq-item-${ci}-${ii}`}
                        >
                          <button
                            onClick={() => setOpenKey(open ? null : key)}
                            className="w-full text-left py-5 flex items-start justify-between gap-4 group"
                            data-testid={`faq-question-${ci}-${ii}`}
                          >
                            <span className="font-display font-extrabold text-base sm:text-lg tracking-tight leading-snug pr-4 transition-colors" style={{ color: open ? "#fff" : "var(--text-1)" }}>
                              {item.q}
                            </span>
                            <span
                              className="flex-shrink-0 w-8 h-8 grid place-items-center border transition-all"
                              style={{
                                borderColor: open ? "var(--border-strong)" : "var(--border)",
                                background: open ? "var(--surface-2)" : "transparent",
                                transform: open ? "rotate(180deg)" : "rotate(0)",
                              }}
                            >
                              <ChevronDown size={14} />
                            </span>
                          </button>
                          <div
                            className="overflow-hidden transition-[max-height,opacity] duration-500"
                            style={{ maxHeight: open ? 600 : 0, opacity: open ? 1 : 0 }}
                          >
                            <p className="text-sm leading-relaxed pb-5 pr-12" style={{ color: "var(--text-2)" }}>
                              {item.a}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
